import React, { useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { CheckCircle2, Send } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialState = {
  vehicle_make: "",
  vehicle_model: "",
  registration_number: "",
  vin_number: "",
  damage_description: "",
  name: "",
  surname: "",
  location: "",
  contact_phone: "",
  contact_email: "",
  insurance: "",
  broker_name: "",
  broker_contact: "",
  policy_number: "",
};

export const QuoteForm = () => {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    const required = [
      "vehicle_make",
      "vehicle_model",
      "registration_number",
      "vin_number",
      "damage_description",
      "name",
      "surname",
      "location",
      "contact_phone",
    ];
    for (const k of required) {
      if (!form[k].trim()) {
        toast.error("Please complete all required fields.");
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      // Strip empty optional fields
      ["contact_email", "insurance", "broker_name", "broker_contact", "policy_number"].forEach((k) => {
        if (!payload[k] || !payload[k].trim()) delete payload[k];
      });
      await axios.post(`${API}/quotes`, payload);
      setSubmitted(true);
      setForm(initialState);
      toast.success("Quote request received. We'll be in touch.");
    } catch (err) {
      console.error(err);
      toast.error("Could not submit. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        className="bg-white border border-[#cfd7db] rounded-md p-10 text-center shadow-lg"
        data-testid="quote-form-success"
      >
        <CheckCircle2 size={48} className="mx-auto text-[#f28a16] mb-4" />
        <h3 className="font-display uppercase text-3xl font-bold mb-2 text-[#111619]">Request received</h3>
        <p className="text-[#3f4b52] max-w-md mx-auto">
          One of our team will get back to you shortly with a quote and next steps.
          For urgent breakage, please WhatsApp or call us directly.
        </p>
        <Button
          className="mt-6 bg-[#f28a16] text-white hover:brightness-110 rounded-md font-display uppercase tracking-widest"
          onClick={() => setSubmitted(false)}
          data-testid="quote-form-new-request"
        >
          Submit another
        </Button>
      </div>
    );
  }

  const inputClass =
    "bg-white border-[#cfd7db] text-[#111619] placeholder:text-[#9aa6ad] focus:border-[#f28a16] focus:ring-1 focus:ring-[#f28a16] rounded-md h-12";

  return (
    <form
      onSubmit={onSubmit}
      className="bg-white border border-[#cfd7db] rounded-md p-6 sm:p-10 shadow-2xl"
      data-testid="quote-form"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Vehicle make *">
          <Input
            value={form.vehicle_make}
            onChange={update("vehicle_make")}
            className={inputClass}
            placeholder="e.g. Toyota"
            data-testid="quote-field-make"
          />
        </Field>
        <Field label="Vehicle model *">
          <Input
            value={form.vehicle_model}
            onChange={update("vehicle_model")}
            className={inputClass}
            placeholder="e.g. Hilux 2.8 GD-6"
            data-testid="quote-field-model"
          />
        </Field>
        <Field label="Registration number *">
          <Input
            value={form.registration_number}
            onChange={update("registration_number")}
            className={inputClass}
            placeholder="e.g. ABC 123 GP"
            data-testid="quote-field-reg"
          />
        </Field>
        <Field label="VIN number *">
          <Input
            value={form.vin_number}
            onChange={update("vin_number")}
            className={inputClass}
            placeholder="17-character chassis number"
            data-testid="quote-field-vin"
          />
        </Field>
        <Field label="Name *">
          <Input
            value={form.name}
            onChange={update("name")}
            className={inputClass}
            placeholder="First name"
            data-testid="quote-field-name"
          />
        </Field>
        <Field label="Surname *">
          <Input
            value={form.surname}
            onChange={update("surname")}
            className={inputClass}
            placeholder="Last name"
            data-testid="quote-field-surname"
          />
        </Field>
        <Field label="Location *">
          <Input
            value={form.location}
            onChange={update("location")}
            className={inputClass}
            placeholder="Town / city / suburb"
            data-testid="quote-field-location"
          />
        </Field>
        <Field label="Contact number *">
          <Input
            value={form.contact_phone}
            onChange={update("contact_phone")}
            className={inputClass}
            placeholder="e.g. 082 000 0000"
            data-testid="quote-field-phone"
          />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Email (optional)">
            <Input
              type="email"
              value={form.contact_email}
              onChange={update("contact_email")}
              className={inputClass}
              placeholder="you@example.com"
              data-testid="quote-field-email"
            />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Description of windscreen breakage / damage *">
            <Textarea
              value={form.damage_description}
              onChange={update("damage_description")}
              rows={5}
              placeholder="Describe the damage — chip, crack, full break, side window, etc. Include anything else useful."
              className={`${inputClass} h-auto py-3`}
              data-testid="quote-field-damage"
            />
          </Field>
        </div>

        {/* Insurance section — all optional */}
        <div className="sm:col-span-2 mt-2">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-px flex-1 bg-[#cfd7db]" />
            <span className="font-display uppercase tracking-widest text-[11px] text-[#3f4b52]">
              Insurance Details — Optional
            </span>
            <div className="h-px flex-1 bg-[#cfd7db]" />
          </div>
          <p className="text-xs text-[#3f4b52] mb-3">
            If your repair will go through insurance, give us a head-start by adding any of the details below. None are required.
          </p>
        </div>
        <Field label="Insurance company">
          <Input
            value={form.insurance}
            onChange={update("insurance")}
            className={inputClass}
            placeholder="e.g. Santam, Discovery, OUTsurance"
            data-testid="quote-field-insurance"
          />
        </Field>
        <Field label="Policy number">
          <Input
            value={form.policy_number}
            onChange={update("policy_number")}
            className={inputClass}
            placeholder="Policy / claim reference"
            data-testid="quote-field-policy"
          />
        </Field>
        <Field label="Broker name">
          <Input
            value={form.broker_name}
            onChange={update("broker_name")}
            className={inputClass}
            placeholder="Broker / agent full name"
            data-testid="quote-field-broker-name"
          />
        </Field>
        <Field label="Broker contact details">
          <Input
            value={form.broker_contact}
            onChange={update("broker_contact")}
            className={inputClass}
            placeholder="Phone or email"
            data-testid="quote-field-broker-contact"
          />
        </Field>
      </div>

      <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <p className="text-xs text-[#3f4b52] max-w-md">
          By submitting, you agree we may contact you about this quote.{" "}
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#f28a16] hover:underline font-semibold"
            data-testid="quote-form-terms-link"
          >
            Terms &amp; Conditions
          </a>{" "}
          apply.
        </p>
        <Button
          type="submit"
          disabled={submitting}
          className="bg-[#f28a16] text-white hover:brightness-110 rounded-md font-display uppercase tracking-widest h-12 px-8 shadow-lg"
          data-testid="quote-form-submit"
        >
          {submitting ? "Sending..." : (<><Send size={16} className="mr-2" /> Request Quote</>)}
        </Button>
      </div>
    </form>
  );
};

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-2">
    <Label className="font-display uppercase tracking-widest text-[11px] text-[#3f4b52]">
      {label}
    </Label>
    {children}
  </div>
);

export default QuoteForm;
